/**
 * Follow a queued Slow (Gemma) chat turn over Firestore (MAR-1390).
 *
 * `POST /api/chat` answers a Slow turn with `{ queued: true, jobId }`: the turn runs on the
 * `research-queue` worker, which appends the chat wire events to `public_rag_jobs/{jobId}.events` and
 * writes the answer to `result.packet.conclusion`. This listens to that doc and feeds every NEW event
 * through `dispatchWireEvent` — the same router, and so the same handlers, the NDJSON reader uses. Each
 * snapshot carries the whole array, so a cursor (`applied`) makes sure an event is applied once.
 *
 * The answer is SAVED by the worker, not here (`server/workers/public-rag/webChatGemma.ts`): the
 * caller's own end-of-turn `updateMessage` writes the same text again, harmlessly.
 *
 * NO WATCHER (`apps/web/AGENTS.md`, "Vue reactivity"): one promise, settled by the snapshot that
 * completes or fails the job, by a listener error, or by `signal`; every one of those paths ends the
 * subscription.
 *
 * STOPPING STOPS THE JOB (MAR-1390). Unsubscribing alone left the worker running, and it then wrote
 * its answer over the message the user had cancelled. So an abort before the job finished also calls
 * `cancelJob`, which asks the app to stop it (`POST /api/chat/queued/:jobId/cancel`). Best-effort and
 * fire-and-forget: the user's stop must not wait on it, and a job that had just finished answers
 * `{ cancelled: false }` harmlessly.
 *
 * PROGRESS (MAR-1424) is not an event: the worker writes the turn's `stage` and its `queuePosition`
 * as FIELDS of the doc, and `onProgress` hands them over on every snapshot until the job settles
 * (`readQueuedChatProgress`). The wire events above still drive the tool chips, as on the stream.
 */
import { dispatchWireEvent, type StreamCallbacks } from '~/composables/useStreamParser'
import { readQueuedChatProgress, type QueuedChatProgress } from '~~/shared/chat/queuedChatTurn'

export { isQueuedChatTurnResponse } from '~~/shared/chat/queuedChatTurn'

/** A queued turn that the worker marked failed. The code is for logs; the user sees the drafted line. */
export class QueuedChatJobFailedError extends Error {
  constructor(readonly errorCode: string) {
    super(`queued chat turn failed (${errorCode})`)
    this.name = 'QueuedChatJobFailedError'
  }
}

export async function streamQueuedChatJob(
  jobId: string,
  callbacks: StreamCallbacks,
  opts: {
    signal?: AbortSignal
    ensureFirestore: () => Promise<unknown>
    cancelJob?: (jobId: string) => Promise<unknown>
    onProgress?: (progress: QueuedChatProgress) => void
  },
): Promise<void> {
  const { signal } = opts
  const stopJob = () => { void opts.cancelJob?.(jobId).catch(() => {}) }
  if (signal?.aborted) {
    stopJob()
    throw new DOMException('The queued chat turn was cancelled.', 'AbortError')
  }
  const [firestore, { doc, onSnapshot }] = await Promise.all([opts.ensureFirestore(), import('firebase/firestore')])

  await new Promise<void>((resolve, reject) => {
    let applied = 0
    let settled = false
    let unsubscribe: (() => void) | null = null
    // Snapshots can arrive while an earlier one's events are still being applied; chain them in order.
    let applying = Promise.resolve()

    function finish(err?: unknown) {
      if (settled) return
      settled = true
      unsubscribe?.()
      signal?.removeEventListener('abort', onAbort)
      if (err) reject(err)
      else resolve()
    }
    function onAbort() {
      if (settled) return
      stopJob()
      finish(new DOMException('The queued chat turn was cancelled.', 'AbortError'))
    }

    async function apply(data: Record<string, any>) {
      if (settled) return
      const events: unknown[] = Array.isArray(data.events) ? data.events : []
      for (; applied < events.length; applied++) {
        // One event a handler cannot apply costs that event, never the turn — as on the stream.
        try { await dispatchWireEvent(events[applied], callbacks) } catch { /* skipped */ }
      }
      if (data.status === 'queued' || data.status === 'running') opts.onProgress?.(readQueuedChatProgress(jobId, data))
      if (data.status === 'completed') {
        const conclusion = data.result?.packet?.conclusion
        if (typeof conclusion === 'string' && conclusion) callbacks.onFinal?.(conclusion)
        finish()
      } else if (data.status === 'failed') {
        finish(new QueuedChatJobFailedError(String(data.errorCode || 'unavailable')))
      }
    }

    signal?.addEventListener('abort', onAbort)
    unsubscribe = onSnapshot(
      doc(firestore as never, 'public_rag_jobs', jobId),
      (snap) => {
        if (!snap.exists()) return
        const data = (snap.data() || {}) as Record<string, any>
        applying = applying.then(() => apply(data)).catch(err => finish(err))
      },
      err => finish(err),
    )
    // A listener can only be torn down once it exists; if the turn settled first, do it now.
    if (settled) unsubscribe()
  })
}
